/**
 * Everything a plat sheet is made of, computed once, drawn by anybody.
 *
 * This was the body of a `useMemo` inside PlatMap. It is geometry and label
 * placement — no React, no DOM — and it is now the single source of truth for
 * both renderers: the SVG on screen and the PDF that goes to the printer. The
 * two can differ in how they draw a line, but never in where the line is or
 * which house numbers were small enough to leave off.
 *
 * Coordinates are the SLC address grid (east, south) from WardMap. Grid units
 * run about 11 feet, and south increases downward, which is already SVG's y
 * direction — no flip needed. PDF flips it back; see plat/sheet.ts.
 */
import { BOUNDARY, gridFromLatLng } from '../components/WardMap';

export interface Parcel {
  /** Rings of [lng, lat]. */
  g: number[][][];
  /** County address, e.g. "3579 S 500 E". */
  a?: string;
  /** Household address keys living on this lot. */
  h?: string[];
  /** Named multi-unit property ("The 500"), where the lot is one. */
  pn?: string;
  /**
   * True when the lot is multi-unit: never label it with a surname, and draw
   * it hatched. Set from the hand-kept property list or from the county's own
   * apartment/condo class, because neither source sees all of them.
   */
  mu?: number;
  /**
   * The assessor's use class, kept only where it is not "Residential" — which
   * is most of the ward, and not worth the bytes. It says how a lot is taxed,
   * not whether anyone lives on it: Twin Oaks Senior Living is "Commercial"
   * and full of members.
   */
  pc?: string;
  /**
   * The incorporated municipality this lot sits in. Not the postal city and
   * never part of an address: the county calls most of this ward Millcreek or
   * South Salt Lake while USPS calls it Salt Lake City, and both are true.
   * Nothing on the map draws it — it is carried so the tools can copy it onto
   * the household, where it answers "whose police and fire".
   */
  mu_city?: string;
  /** Group key — parcels sharing it are one property to visit. */
  k?: string;
  /** County's dwelling count, present only when the lot holds more than one. */
  hc?: number;
  /**
   * Which sector this lot is in, as indices into PlatData.sector_macros /
   * sector_micros. Every lot inside the ward has one, whether a member lives
   * on it or not — that is what lets a sheet draw the edge of its own patch
   * and grey what is outside it. Absent on the ring of lots the asset keeps
   * beyond the boundary, which belong to somebody else's ward.
   */
  sm?: number;
  si?: number;
}
export interface Road {
  n: string;
  g: number[][];
  /**
   * Block address range: [fromLeft, toLeft, fromRight, toRight], left and
   * right being relative to the direction the line is drawn. Nothing on the
   * map reads it — it is here because the matcher that decides which lot a
   * household sits on runs against this same file, and uses the ranges to
   * tell the two sides of a street apart. See tools/matching.py.
   */
  r?: number[];
}
export interface PlatData {
  generated: string;
  /** Sector names, stored once and referenced by index from each parcel. */
  sector_macros?: string[];
  sector_micros?: string[];
  parcels: Parcel[];
  roads: Road[];
}
export interface PlatHousehold {
  /**
   * The household's own id. Not how it is placed on a lot — `key` does that —
   * but how a sheet limited to a search or report tells its households from
   * the rest. Optional because the incident packet builds households out of
   * addresses it has no roster row for, and never limits a sheet.
   */
  id?: string;
  /**
   * Uppercased, whitespace-collapsed first address line. Identifies the
   * household's own door: the house number the roster prints, the sort order
   * on a shared lot, and the fallback join described on `parcelKey`.
   */
  key: string;
  /**
   * The county lot, from `households.parcel_key` — matches `Parcel.k`.
   *
   * This is the join. It used to be done the other way round, by looking this
   * household's `key` up in the list of addresses baked into each parcel as
   * `h`; that made the link a property of a build of parcels.json rather than
   * of the household, so a family who moved in after the last build had no
   * lot until somebody re-fetched the county's parcel layer and deployed. The
   * sync now recomputes it from the effective address, so an address fix
   * reaches the map on the next sync.
   *
   * Null where the household has no DB row to carry one — the incident packet
   * builds these out of site addresses — and those fall back to the old `h`
   * lookup. That fallback is the only remaining reader of `h`, and has to be
   * replaced before `h` can come out of the asset.
   */
  parcelKey?: string | null;
  /**
   * The unit, where the household has one. Deliberately not part of `key`:
   * the key is what joins a household to a county parcel, and the county maps
   * a building, not its doors. The roster prints both.
   */
  unit?: string | null;
  label: string;
  lat: number | null;
  lng: number | null;
  /** Age of the oldest member, for choosing between households on one lot. */
  oldestAge?: number | null;
  macro?: string | null;
  micro?: string | null;
}

/** What the sheet is about. Everything else on it is context. */
export type PlatFocus =
  | { kind: 'ward' }
  | { kind: 'macro'; name: string }
  | { kind: 'micro'; name: string };

/** Printable area of a Letter sheet at 0.4in margins, in CSS px. */
export const SHEET_LONG = 979;
export const SHEET_SHORT = 739;
/** Every lot line, at one weight. Shading says who lives there, not stroke. */
export const LOT_LINE = 0.5;
/** Smallest type that survives a laser printer and a car dashboard. */
export const MIN_LABEL_PX = 5.2;
export const STREET_LABEL_PX = 7;
/**
 * The hatch that marks a multi-unit building: spacing, weight and how dark,
 * in CSS px. Shared so the screen and the printed sheet hatch alike.
 *
 * Deliberately weak. It has to read as texture at a glance and never compete
 * with a house number sitting on top of it, and it has to survive on grey as
 * well as on white — a lot can be both ours and an apartment block.
 */
/**
 * How faint a lot outside the sector is drawn — its lines, and its number.
 *
 * A sector has no line round it. It was drawn with one for a while, traced
 * from the lots and later grown through the streets, and neither read as a
 * walker wants: the first came out in a dozen pieces because streets cut a
 * sector into separate blocks, and the second annexed a tenth of the
 * neighbours because a line down the middle of a road has the far kerb inside
 * it.
 *
 * What a walker actually asks is "is this one mine", and that is a question
 * about a house, not about a boundary. So the answer is on the house: yours
 * in full black, everyone else's faint enough to read as background. Nothing
 * has to be traced, nothing can be in the wrong place, and a sector in five
 * disconnected pieces needs no explaining.
 *
 * Only the lot lines need a grey. A lot outside the sector carries no number
 * at all — see the label loops — so there is no faint text to tune.
 */
export const OUTSIDE_GRAY = 0.78;

export const HATCH_PX = 3.2;
export const HATCH_LINE = 0.4;
export const HATCH_OPACITY = 0.42;

export type XY = { x: number; y: number };

/** The traced ward boundary, in grid units, ready to draw. */
export const WARD_BOUNDARY: XY[] = BOUNDARY.map(([x, y]) => ({ x, y }));
export type Box = { x0: number; y0: number; x1: number; y1: number };

const project = (lng: number, lat: number): XY => {
  const { east, south } = gridFromLatLng(lat, lng);
  return { x: east, y: south };
};

const growBox = (b: Box, p: XY) => {
  b.x0 = Math.min(b.x0, p.x); b.y0 = Math.min(b.y0, p.y);
  b.x1 = Math.max(b.x1, p.x); b.y1 = Math.max(b.y1, p.y);
};
const emptyBox = (): Box => ({ x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });
const boxOf = (rings: XY[][]): Box => {
  const b = emptyBox();
  for (const r of rings) for (const p of r) growBox(b, p);
  return b;
};

/** House number only — the street is already labelled on the road. */
function houseNumber(address: string | undefined): string | null {
  const m = /^(\d+)/.exec((address ?? '').trim());
  return m ? m[1] : null;
}

/** "Manning, Nathan & Timber Jordan" -> "Manning". */
function lastName(directoryName: string | undefined): string | null {
  const n = (directoryName ?? '').trim();
  if (!n) return null;
  const [surname] = n.split(',');
  return surname.trim() || null;
}

/**
 * Tolerance ring in grid units (~48 ft), matching `tools/ward_boundary.inside`.
 *
 * For a *household*, whose address may geocode to a street centreline and so
 * land on the boundary by construction. Never for a parcel: a lot's centroid
 * is a surveyed location, and padding it pulls in lots that are wholly across
 * the street in the next ward — which is why the default here is no pad at
 * all, and only the household dots opt in.
 */
export const WARD_PAD = 6;

/** Even-odd point-in-polygon against the ward boundary, in grid units. */
export function insideWardGrid(x: number, y: number, pad = 0): boolean {
  let c = false;
  for (let i = 0, j = BOUNDARY.length - 1; i < BOUNDARY.length; j = i++) {
    const [xi, yi] = BOUNDARY[i];
    const [xj, yj] = BOUNDARY[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  if (c || pad <= 0) return c;
  // Outside the polygon, but perhaps only by the width of the kerb.
  for (let i = 0, j = BOUNDARY.length - 1; i < BOUNDARY.length; j = i++) {
    const [xi, yi] = BOUNDARY[i];
    const [xj, yj] = BOUNDARY[j];
    const dx = xj - xi, dy = yj - yi;
    const l2 = dx * dx + dy * dy;
    const t = l2 === 0 ? 0
      : Math.max(0, Math.min(1, ((x - xi) * dx + (y - yi) * dy) / l2));
    const ex = x - (xi + t * dx), ey = y - (yi + t * dy);
    if (ex * ex + ey * ey <= pad * pad) return true;
  }
  return false;
}

/**
 * Outline of a set of parcels: every edge that only one of them owns.
 *
 * A polygon union in the only case that matters here, without the machinery
 * of one. Two parcels that abut share an edge exactly — the county's rings
 * meet, and rounding preserves that — so an edge seen twice is interior and
 * an edge seen once is on the boundary. Where rings do not quite meet, the
 * stray interior line survives and the shape is still right, which is the
 * right way for it to fail.
 */
function outlineOf(parcels: { rings: XY[][] }[]): Seg[] {
  const count = new Map<string, number>();
  const edges: [XY, XY, string][] = [];
  for (const p of parcels) {
    for (const ring of p.rings) {
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i], b = ring[(i + 1) % ring.length];
        const ka = `${a.x.toFixed(4)},${a.y.toFixed(4)}`;
        const kb = `${b.x.toFixed(4)},${b.y.toFixed(4)}`;
        const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
        count.set(key, (count.get(key) ?? 0) + 1);
        edges.push([a, b, key]);
      }
    }
  }
  return edges
    .filter(([, , key]) => count.get(key) === 1)
    .map(([a, b]) => [a, b] as Seg);
}

/**
 * A line segment. Outlines are kept as segments rather than as an SVG path
 * string so the PDF renderer does not have to parse one back out again; the
 * SVG renderer joins them on the way to the DOM.
 */
export type Seg = [XY, XY];

export const segPath = (segs: Seg[]): string =>
  segs.map(([a, b]) => `M${a.x} ${a.y}L${b.x} ${b.y}`).join('');

const centroid = (rings: XY[][]): XY => {
  const pts = rings[0] ?? [];
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: x / (pts.length || 1), y: y / (pts.length || 1) };
};

/**
 * Pad a box by a margin. Nothing more.
 *
 * This used to stretch the box out to the sheet's aspect ratio so the map
 * always filled the page. That letterboxed the content: the ward is about
 * three and a half times wider than it is tall, so a third of the sheet was
 * map and two thirds were white space inside the map's own border. Keeping
 * the content's own shape and fitting *that* to the page draws it as large as
 * the paper allows, and leaves the spare room below the map where the address
 * list can use it.
 */
function padBox(b: Box, margin: number): Box {
  return { x0: b.x0 - margin, y0: b.y0 - margin,
           x1: b.x1 + margin, y1: b.y1 + margin };
}

/** Printable area of a Letter sheet at 0.4in margins, less room for the heading. */
const PAGE_LONG_IN = 10.2;
const PAGE_SHORT_IN = 7.7;
const HEADING_IN = 0.35;

/** Largest width/height in inches that fits the page at this aspect ratio. */
function printSize(aspect: number, landscape: boolean) {
  const availW = landscape ? PAGE_LONG_IN : PAGE_SHORT_IN;
  const availH = (landscape ? PAGE_SHORT_IN : PAGE_LONG_IN) - HEADING_IN;
  const h = Math.min(availH, availW / aspect);
  return { w: h * aspect, h };
}

export interface PlatParcel {
  rings: XY[][];
  address?: string;
  /** Holds a household this sheet is about. */
  mine: boolean;
  keys: string[];
  /** Property name, where the lot is a named multi-unit building. */
  name: string | null;
  /** Households living here: house number and surname, at most two. */
  roster: { num: string | null; name: string }[];
  mu: boolean;
  /**
   * Public land with no door on it — a cul-de-sac bulb, a drainage sliver,
   * the corner the city kept when it widened the street. The county carries
   * these as parcels and gives them house numbers, so they arrive here
   * looking exactly like a lot: 1199 E Ridgedale Ln is 1,717 sq ft of
   * Ridgedale Ln itself. The shape is drawn, because it is part of how the
   * street reads; the number is not, because nobody can knock on it.
   */
  roadFurniture: boolean;
  k: string | null;
  c: XY;
  /**
   * Draw this parcel's own edges. False for a lot that is one of several
   * making up a property — the property's merged outline is drawn instead.
   */
  outlined: boolean;
  /**
   * Shade it. Follows the whole property, not the parcel: only one of the
   * three lots at 1211 E Ridgedale Ln matched a household, and shading just
   * that one left the rest of the same property white, as if the boundary we
   * had just merged away still meant something.
   *
   * True on the ward sheet too, where it is the only thing on the map that
   * says where the members are: at that scale the house numbers are gone, so
   * shading carries the sheet on its own.
   */
  filled: boolean;
  /**
   * This lot is in the sector the sheet is about. False for a neighbour, and
   * false for everything on the ward sheet's ring beyond the boundary.
   *
   * Not the same as `filled`, which asks whether a household this sheet is
   * about lives here. Most lots in a sector have no member on them at all.
   */
  inSector: boolean;
  /**
   * Hatch it: the lot is a multi-unit building. Diagonal stripes rather than
   * a second shade, because shading already means "a household this sheet is
   * about lives here" and the two facts are independent — an apartment block
   * may hold six of ours, one, or none.
   *
   * What it tells a walker is that one door on this footprint is not one
   * household: the number on the map gets them to the building and the unit
   * numbers are in the roster.
   */
  striped: boolean;
}

export interface PlatLabel {
  x: number; y: number; text: string; bold?: boolean;
  /**
   * Print it on a white halo. Set only for labels on a hatched lot, where
   * five-point type crossed by stripes is what a walker has to read. Every
   * other label is on plain white or plain grey and wants no halo — a halo
   * on all sixteen hundred would eat the lot lines between close numbers.
   */
  halo?: boolean;
}

export interface PlatModel {
  parcels: PlatParcel[];
  roads: { n: string; pts: XY[] }[];
  frame: Box;
  labels: PlatLabel[];
  /** Lots too small to letter: numbered on the map, spelled out beneath it. */
  listed: { n: number; address: string }[];
  /** In the ward, but no county parcel matched — drawn as a dot. */
  orphans: (PlatHousehold & XY)[];
  /** Outside the ward boundary entirely — there is nowhere to draw them. */
  outside: PlatHousehold[];
  unitsPerPx: number;
  streetLabels: { name: string; x: number; y: number; deg: number }[];
  landscape: boolean;
  /**
   * A multi-parcel property drawn as one shape. `inSector` because these are
   * the only lot lines the parcel loop does not draw — a grouped parcel is
   * `outlined: false` and gets its edges from here — so without it every
   * condo and every multi-lot property outside the sector came out at full
   * black while its neighbours faded.
   */
  merged: { d: Seg[]; mine: boolean; inSector: boolean }[];
  wardView: boolean;
  /** Size in inches at which this map fills a Letter page. */
  paper: { w: number; h: number };
}

export interface PlatOptions {
  data: PlatData;
  /**
   * Every household in the ward, each carrying its sector. All of them are
   * needed even on a single-sector sheet: two families of different sectors
   * share a lot often enough that a roster built from the focus alone would
   * drop one of them.
   */
  households: PlatHousehold[];
  focus: PlatFocus;
  /**
   * Household ids this sheet is limited to — a search or report handed to the
   * map. Absent means the sheet is about its whole sector, as it always was.
   *
   * A limit narrows what the sheet is *about*, never what it is drawn from:
   * `households` still arrives whole, so a lot shared by a matched household
   * and an unmatched one is still one lot, drawn once.
   */
  limitTo?: ReadonlySet<string>;
  orientation?: 'auto' | 'portrait' | 'landscape';
  /** Surnames and property names under the house numbers. */
  showNames?: boolean;
  /** Households with no matched parcel, drawn as a dot. Widens the frame. */
  /**
   * Width of the drawing area, in CSS px, portrait and landscape. Every
   * label-fitting decision is measured against it, so a renderer working at a
   * different size passes its own and gets labels sized for its page.
   */
  sheetPx?: { portrait: number; landscape: number };
}

/**
 * The stretches of a polyline that fall inside the frame, in drawing order.
 *
 * A street name belongs on the part of the street the reader can see. Keeping
 * whole vertices instead — the in-frame ones — gets that wrong twice: a road
 * whose two vertices straddle the sheet has no in-frame vertex at all, and a
 * road that leaves the sheet and comes back joins across the gap, so the name
 * is printed in the gap. Clipping each edge to the frame and re-joining the
 * pieces that still meet gives the visible run itself.
 */
function clipToFrame(pts: XY[], f: Box): XY[][] {
  const runs: XY[][] = [];
  let open: XY[] | null = null;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const dx = b.x - a.x, dy = b.y - a.y;
    // Liang–Barsky: the sub-range of [0,1] along a→b that is inside f.
    const ps = [-dx, dx, -dy, dy];
    const qs = [a.x - f.x0, f.x1 - a.x, a.y - f.y0, f.y1 - a.y];
    let t0 = 0, t1 = 1, out = false;
    for (let k = 0; k < 4 && !out; k++) {
      if (ps[k] === 0) { if (qs[k] < 0) out = true; continue; }
      const r = qs[k] / ps[k];
      if (ps[k] < 0) { if (r > t1) out = true; else if (r > t0) t0 = r; }
      else if (r < t0) out = true; else if (r < t1) t1 = r;
    }
    if (out || t1 <= t0) { open = null; continue; }
    const p0 = { x: a.x + t0 * dx, y: a.y + t0 * dy };
    const p1 = { x: a.x + t1 * dx, y: a.y + t1 * dy };
    if (open && Math.hypot(open[open.length - 1].x - p0.x,
                           open[open.length - 1].y - p0.y) < 1e-6) {
      open.push(p1);
    } else {
      open = [p0, p1];
      runs.push(open);
    }
    // An edge clipped short of its far end leaves the frame here, so the next
    // edge starts a new run even if it comes straight back in.
    if (t1 < 1) open = null;
  }
  return runs;
}

/**
 * Join polylines that meet end to end.
 *
 * The county splits a street at every intersection, so "800 E" arrives as a
 * dozen separate pieces. Measured a piece at a time, a street running the
 * length of the sheet looks like a dozen stubs and goes unlabelled; joined,
 * it is one road again and the name sits in the middle of it.
 *
 * Pieces that do not meet stay apart, which is the point: a street with a
 * genuine gap in it is two runs, and the name goes on the longer.
 */
function chainRuns(runs: XY[][], eps: number): XY[][] {
  const out = runs.map((r) => [...r]);
  const near = (p: XY, q: XY) => Math.hypot(p.x - q.x, p.y - q.y) <= eps;
  const rev = (r: XY[]) => [...r].reverse();
  for (let joined = true; joined;) {
    joined = false;
    for (let i = 0; i < out.length && !joined; i++) {
      for (let j = i + 1; j < out.length && !joined; j++) {
        const a = out[i], b = out[j];
        if (near(a[a.length - 1], b[0])) out[i] = [...a, ...b.slice(1)];
        else if (near(a[a.length - 1], b[b.length - 1])) out[i] = [...a, ...rev(b).slice(1)];
        else if (near(a[0], b[b.length - 1])) out[i] = [...b, ...a.slice(1)];
        else if (near(a[0], b[0])) out[i] = [...rev(a), ...b.slice(1)];
        else continue;
        out.splice(j, 1);
        joined = true;
      }
    }
  }
  return out;
}

/** Distance to each vertex of a polyline from its start. */
function arcLengths(pts: XY[]): number[] {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  }
  return cum;
}

/** The point `d` along a polyline, measured in length rather than vertices. */
function pointAt(pts: XY[], cum: number[], d: number): XY {
  const total = cum[cum.length - 1];
  const s = Math.min(Math.max(d, 0), total);
  let i = 1;
  while (i < cum.length - 1 && cum[i] < s) i++;
  const run = cum[i] - cum[i - 1];
  const f = run > 1e-9 ? (s - cum[i - 1]) / run : 0;
  return { x: pts[i - 1].x + f * (pts[i].x - pts[i - 1].x),
           y: pts[i - 1].y + f * (pts[i].y - pts[i - 1].y) };
}

/**
 * The runs of interior a horizontal line at `y` crosses inside a shape.
 *
 * Parcels of one property abut, and the county's rings share those edges
 * exactly, so a row crossing two of them comes back as two runs meeting at a
 * point. They are joined: the property is one shape and a label may cross the
 * internal line, which is not drawn.
 */
function interiorRuns(rings: XY[][], y: number): [number, number][] {
  const xs: number[] = [];
  for (const r of rings) {
    for (let j = 0; j < r.length; j++) {
      const p = r[j], q = r[(j + 1) % r.length];
      if ((p.y > y) !== (q.y > y)) {
        xs.push(p.x + ((y - p.y) * (q.x - p.x)) / (q.y - p.y));
      }
    }
  }
  xs.sort((m, n) => m - n);
  const runs: [number, number][] = [];
  for (let j = 0; j + 1 < xs.length; j += 2) {
    const prev = runs[runs.length - 1];
    if (prev && xs[j] - prev[1] < 1e-6) prev[1] = xs[j + 1];
    else runs.push([xs[j], xs[j + 1]]);
  }
  return runs;
}

/**
 * Where a horizontal label sits inside a shape, and how much room it has.
 *
 * The bounding-box centre is the obvious anchor and it is wrong often enough
 * to matter: it is not inside an L-shaped lot, it is not inside a flag lot,
 * and for a property whose parcels do not touch it lands between them. 1240 E
 * Lavon Cir is a lot plus a detached sliver, and the midpoint of the two is
 * the neighbour's front garden — which is where the label was printed.
 *
 * Scan rows across the shape and take the widest unbroken run of interior
 * found on any of them: the longest horizontal line the shape can hold, so
 * the line of text with the most room either side. Ties go to the row nearest
 * the middle — on a plain rectangular lot every row is equally wide, and
 * taking the first would pin every label to the top edge.
 *
 * `need` is the width the text actually wants, and it changes the question
 * from "where is this lot widest" to "where in this lot does the text fit".
 * Those differ on any lot that is not a rectangle: 1258 E Ridgedale Ln is a
 * wedge widening away from the road, so its widest row is its bottom edge and
 * the name was printed half on the pavement below. Once a band of rows is
 * known to hold the text, the middle of that band is the place to centre it.
 */
function labelAnchor(rings: XY[][], need = 0, rows = 15): { x: number; y: number; w: number } {
  const b = boxOf(rings);
  const midY = (b.y0 + b.y1) / 2;
  const rowY = (i: number) => b.y0 + ((b.y1 - b.y0) * i) / (rows + 1);
  const runs: ([number, number][])[] = [];
  for (let i = 0; i <= rows + 1; i++) runs[i] = interiorRuns(rings, rowY(i));

  // Score a row by the width it *sustains* onto the rows either side, not by
  // its own width. A label is a block of lines, so a one-row bulge is no use:
  // 3579 S 500 E is a narrow front lot grouped with a wide rear one, and the
  // rear lot's flare made the top row twice the width of every other, which
  // pinned a three-line label to the north edge with room to spare below it.
  // Intersecting with the neighbours costs the bulge exactly what it cannot
  // deliver, and leaves every ordinary rectangular lot scoring as before.
  let best = { x: (b.x0 + b.x1) / 2, y: midY, w: 0 };
  let widest = { ...best };
  // Bands of consecutive rows wide enough for `need`, built as the scan goes.
  const holds: { i0: number; i1: number; rows: { x: number; y: number; w: number }[] }[] = [];
  for (let i = 1; i <= rows; i++) {
    const y = rowY(i);
    for (const [x0, x1] of runs[i]) {
      const plain = x1 - x0;
      if (plain > widest.w) widest = { x: (x0 + x1) / 2, y, w: plain };
      let lo = x0, hi = x1;
      for (const j of [i - 1, i + 1]) {
        let bestOverlap: [number, number] | null = null;
        for (const [a, c] of runs[j] ?? []) {
          const s0 = Math.max(a, lo), s1 = Math.min(c, hi);
          if (s1 - s0 > (bestOverlap ? bestOverlap[1] - bestOverlap[0] : 0)) {
            bestOverlap = [s0, s1];
          }
        }
        if (!bestOverlap) { lo = 0; hi = -1; break; }
        [lo, hi] = bestOverlap;
      }
      const w = hi - lo;
      if (w <= 0) continue;
      const row = { x: (lo + hi) / 2, y, w };
      const better = w > best.w * 1.02
        || (w > best.w * 0.98 && Math.abs(y - midY) < Math.abs(best.y - midY));
      if (better) best = { ...row, w: Math.max(w, best.w) };
      // A band is a corridor, so it only continues into a run that overlaps
      // the one above it. An L-shaped lot has two runs on some rows, and
      // stringing them together by row number alone would call the two arms
      // of the L one band.
      if (w >= need) {
        const prev = holds[holds.length - 1];
        const last = prev?.rows[prev.rows.length - 1];
        if (prev && last && prev.i1 === i - 1
            && Math.abs(row.x - last.x) < (row.w + last.w) / 2) {
          prev.i1 = i; prev.rows.push(row);
        } else holds.push({ i0: i, i1: i, rows: [row] });
      }
    }
  }

  // The tallest run of consecutive rows that all hold the text, and the row
  // halfway down it. Tallest rather than widest: a band two rows deep can
  // carry a line of text but not a block of them, and the extra width a
  // pinched corner offers is worth less than the room to sit upright in.
  // Ties go to the band nearest the middle of the lot.
  if (need > 0 && holds.length) {
    const mid = (h: typeof holds[number]) => (rowY(h.i0) + rowY(h.i1)) / 2;
    const pick = holds.reduce((m, h) => {
      const depth = h.i1 - h.i0, was = m.i1 - m.i0;
      return depth > was || (depth === was && Math.abs(mid(h) - midY) < Math.abs(mid(m) - midY))
        ? h : m;
    });
    const target = mid(pick);
    const row = pick.rows.reduce((m, r) =>
      Math.abs(r.y - target) < Math.abs(m.y - target) ? r : m);
    return row;
  }
  // A lot too shallow to sustain anything across three rows still needs an
  // anchor; fall back to the widest single row, which is what this was.
  return best.w > 0 ? best : widest;
}

/**
 * Slide one line of text onto the row it is actually drawn on.
 *
 * The anchor is chosen once for the label, but a two-line label puts its
 * second line a font-height below it and even a single number is nudged off
 * it. On a wedge-shaped lot that offset is enough to leave the polygon. So
 * each line is placed against the row it is really drawn on: the offset is
 * walked back toward the anchor until the row has interior to sit in, and
 * the text is then kept near the anchor where the row has space for it and
 * pushed inside where it does not.
 */
/**
 * One column for a label that runs to two lines.
 *
 * Placing each line against its own row independently would stagger them —
 * the number over here, the surname a little to the left — which reads as two
 * labels rather than one. Take the x-range each row allows and use the range
 * common to both, so the lines stack.
 */
/**
 * The lines a lot's label runs to.
 *
 * One house number and one surname per household, except that households
 * sharing a number share it: 1237 E Lorraine Dr holds three families under
 * one door number, and repeating "1237" above each name would say the lot has
 * three addresses when it has one.
 *
 *   1237          1241
 *   Ashton        Hill
 *   Wilson        1245
 *                 Chamberlain
 */
function labelLines(num: string | null,
                    roster: { num: string | null; name: string }[]): string[] {
  if (!roster.length) return num ? [num] : [];
  const nums = [...new Set(roster.map((e) => e.num ?? num))];
  if (nums.length === 1) return [nums[0] ?? num, ...roster.map((e) => e.name)]
    .filter((t): t is string => !!t);
  return roster.flatMap((e) => [e.num ?? num, e.name])
    .filter((t): t is string => !!t);
}

/**
 * Vertical offsets for a block of `n` lines, from the label's anchor row.
 *
 * The constants reproduce the old two-line spacing exactly at n = 2, so the
 * thousands of lots that carry a number over a surname do not all shift when
 * a handful of them grow a third line.
 */
function lineOffsets(n: number, fontUnits: number): number[] {
  const gap = 1.15 * fontUnits;
  return Array.from({ length: n },
    (_, i) => (i - (n - 1) / 2) * gap + 0.425 * fontUnits);
}

/**
 * Rows of a multi-line label, or null when the lot cannot carry them apart.
 *
 * `placeLine` pulls a row that finds no interior run at its own offset back
 * toward the anchor. For one line that is the right answer; for a block it
 * lands the row on the one above it, and 404 E 3335 S — a sliver carrying a
 * number over two surnames — came out as three lines of text in the space of
 * one. A block that cannot keep its rows apart is not a block that fits, so
 * say so and let the caller fall back to a shorter label.
 */
function placeBlock(rings: XY[][], x: number, ay: number, dys: number[], w: number): XY[] | null {
  const rows = dys.map((dy) => placeLine(rings, x, ay, dy, w));
  for (let i = 1; i < rows.length; i++) {
    const want = Math.abs(dys[i] - dys[i - 1]);
    if (Math.abs(rows[i].y - rows[i - 1].y) < want * 0.9) return null;
  }
  let lo = -Infinity, hi = Infinity;
  for (const r of rows) {
    const run = interiorRuns(rings, r.y).find(([a, b]) => r.x >= a && r.x <= b);
    if (!run) continue;
    lo = Math.max(lo, run[0] + w / 2);
    hi = Math.min(hi, run[1] - w / 2);
  }
  // No column suits both rows — the lot narrows between them. Let the lines
  // stagger rather than forcing a column: each is then inside its own row,
  // and a label that steps is easier to read than one printed on the
  // neighbour's garden.
  if (lo > hi) return rows;
  const col = Math.min(Math.max(x, lo), hi);
  return rows.map((r) => ({ x: col, y: r.y }));
}

function placeLine(rings: XY[][], x: number, ay: number, dy: number, w: number): XY {
  let y = ay + dy;
  let runs = interiorRuns(rings, y);
  for (let k = 0.6; !runs.length && k > 0; k -= 0.3) {
    y = ay + dy * k;
    runs = interiorRuns(rings, y);
  }
  if (!runs.length) return { x, y: ay };
  const [a, b] = runs.find(([lo, hi]) => x >= lo && x <= hi)
    ?? runs.reduce((m, r) => (r[1] - r[0] > m[1] - m[0] ? r : m));
  if (b - a <= w) return { x: (a + b) / 2, y };
  return { x: Math.min(Math.max(x, a + w / 2), b - w / 2), y };
}

export function buildPlat({
  data, households, focus, limitTo, orientation = 'auto',
  showNames = true, sheetPx,
}: PlatOptions): PlatModel {
  /** Is this household one of the sheet's own, before the sector is asked? */
  const limited = (h: PlatHousehold) =>
    !limitTo || (h.id != null && limitTo.has(h.id));
  // Every household at an address, not one per address. Three families share
  // 1237 E Lorraine Dr and a Map keyed by address kept only the last of them,
  // so the seniority sort below ran over a single arbitrary household and
  // could never pick the senior one.
  // Keyed by the lot, not the address: two households on one lot answer to
  // one parcel key, and a household with no parcel key of its own is filed
  // under its address for the `h` fallback below.
  const lotKey = (h: PlatHousehold) => h.parcelKey || h.key;
  const byKey = new Map<string, PlatHousehold[]>();
  for (const h of households) {
    const k = lotKey(h);
    byKey.set(k, [...(byKey.get(k) ?? []), h]);
  }
  // Households this sheet is about: the only ones shaded, named and listed.
  const inFocus: PlatHousehold[] = households.filter((h) => limited(h) && (
    focus.kind === 'ward' ? true
      : focus.kind === 'macro' ? h.macro === focus.name
        : h.micro === focus.name));
  const focusKeys = new Set<string>(inFocus.map(lotKey));

  /**
   * Who lives at these addresses: one entry per surname, with the house
   * number that surname answers to, alphabetical, at most two.
   *
   * The number comes from the household's own address rather than the
   * parcel's, because a duplex is one lot with two doors — 1245 E Lavon Cir
   * is the Chamberlains' number and the Hills next door are 1241, and a
   * walker needs the number they will actually see.
   *
   * Two households of one family share a line: Paul and Zach Ashton are both
   * at 1237 E Lorraine Dr and the lot is the Ashtons' either way.
   */
  // Tax-exempt public land, nobody living on it, and less ground than a real
  // lot. Both halves are needed: an occupied lot can be tiny — a condo
  // footprint here is 197 sq ft — so "no household" is what carries the rule,
  // and the area only keeps the parks, schools and church campuses labelled.
  //
  // The threshold sits in a gap the ward's own data leaves: sixteen such
  // parcels run 30 to 3,700 sq ft, and the next one up is 8,800 — a park.
  const FURNITURE_SQFT = 5000;
  const SQFT_PER_UNIT = 64;            // a grid unit is ~8 ft on a side
  const ringArea = (rings: XY[][]) => rings.reduce((tot, r) => {
    let a = 0;
    for (let i = 0; i < r.length; i++) {
      const q = r[i], n = r[(i + 1) % r.length];
      a += q.x * n.y - n.x * q.y;
    }
    return tot + Math.abs(a) / 2;
  }, 0);
  const roadFurniture = (p: Parcel, rings: XY[][]) =>
    !!p.pc?.startsWith('Tax Exempt')
    && !(p.h?.length) && !(p.k && byKey.has(p.k))
    && ringArea(rings) * SQFT_PER_UNIT < FURNITURE_SQFT;

  const rosterOn = (keys: string[]): { num: string | null; name: string }[] => {
    const out: { num: string | null; name: string }[] = [];
    for (const k of keys) {
      for (const h of byKey.get(k) ?? []) {
        // Both families on a shared lot are named, because both live there —
        // unless the sheet is limited, in which case the neighbour who did not
        // match is not one of the names this sheet is answering for.
        if (!limited(h)) continue;
        const name = lastName(h.label);
        if (!name || out.some((e) => e.name === name)) continue;
        // h.key, not the lot key: a duplex is one parcel with two doors, and
        // "1237 before 1241" below is the order a walker meets them in. The
        // lot's own number would print the same digits twice.
        out.push({ num: houseNumber(h.key), name });
      }
    }
    // House number first, surname only to break a tie. A lot holding two
    // families reads as a walker approaches it — 1237 before 1241 — and
    // sorting by surname put Nyatsambo after Beck regardless of which door
    // came first. This also decides *which* two survive the cap below, so a
    // lot with three families keeps the two lowest numbers rather than the
    // two earliest in the alphabet.
    return out
      .sort((a, b) => {
        const an = a.num === null ? Infinity : Number(a.num);
        const bn = b.num === null ? Infinity : Number(b.num);
        return an - bn || a.name.localeCompare(b.name);
      })
      .slice(0, 2);
  };
  // The county maps one row per *building*, so a lot with two dwellings on it
  // arrives twice with byte-identical geometry — 439 E Scott Ave is a duplex
  // and ships as two rows. `refresh_parcels` keeps both on purpose, because
  // the second row is what matches a household at the low end of an address
  // range, but for drawing they are one lot: two copies of a ring make every
  // edge look shared, so `outlineOf` discards the whole outline and
  // `interiorRuns` pairs every crossing with its twin and reports no interior
  // at all, which loses the label's anchor with it. Collapse them here, into
  // one row carrying the union of the household keys, so nothing downstream
  // has to know the county counts buildings.
  const byShape = new Map<string, Parcel>();
  for (const p of data.parcels) {
    const shape = JSON.stringify(p.g);
    const seen = byShape.get(shape);
    if (!seen) {
      byShape.set(shape, { ...p, h: p.h ? [...p.h] : undefined });
      continue;
    }
    if (p.h?.length) seen.h = [...new Set([...(seen.h ?? []), ...p.h])];
    seen.a ??= p.a;
    seen.k ??= p.k;
    seen.pn ??= p.pn;
    seen.mu ??= p.mu;
    seen.hc ??= p.hc;
  }

  const all = [...byShape.values()].map((p) => {
    const rings = p.g.map((r) => r.map(([lng, lat]) => project(lng, lat)));
    // The lot's own key first — that is what a household's parcel_key points
    // at — then the addresses baked into `h`, which still place the synthetic
    // households the incident packet builds from site addresses.
    const keys = [...new Set([...(p.k ? [p.k] : []), ...(p.h ?? [])])];
    const mine = keys.filter((k) => focusKeys.has(k));

    // One label per lot, chosen in that order:
    //
    // A named multi-unit property labels itself. Nobody calls a 24-unit
    // building by whichever resident is oldest, and a surname on it would
    // tell a walker the wrong thing about what they are approaching.
    //
    // Otherwise the households living there, one per line, at most two.
    // Where a lot holds two families a walker wants both names on the door —
    // 1237 E Lorraine Dr is the Ashtons' with a daughter's family, the
    // Wilsons, in it, and either name gets somebody answering. Two is the cap
    // because a third does not fit and a lot with three families is better
    // read from the address list than from a crowded label.
    //
    // A multi-unit building never takes a resident's surname, named or not.
    // Where it has no name the number is the honest label: putting one of
    // thirty-two households on the front of the building is worse than
    // saying nothing about who lives there.
    const roster = showNames && !p.pn && !p.mu ? rosterOn(mine) : [];
    const name: string | null = showNames ? (p.pn ?? null) : null;
    return { rings, address: p.a, mine: mine.length > 0, keys, name, roster,
             mu: !!p.mu, roadFurniture: roadFurniture(p, rings),
             k: p.k ?? null, c: centroid(rings),
             // Carried through so a sheet can tell its own sector's lots from
             // the neighbours'. Undefined beyond the ward boundary.
             sm: p.sm, si: p.si };
  });

  // Clip to the ward. Everything past the boundary is another ward's problem,
  // and drawing it makes the sheet harder to read and the edge ambiguous.
  // Which lots this sheet is about. Not "who lives here" — most lots in a
  // sector have no member on them — but "is this one mine to walk".
  const macroOf = data.sector_macros ?? [];
  const microOf = data.sector_micros ?? [];
  const inSector = (p: { sm?: number; si?: number }) => {
    if (focus.kind === 'ward') return true;
    if (focus.kind === 'macro') return macroOf[p.sm ?? -1] === focus.name;
    return microOf[p.si ?? -1] === focus.name;
  };

  /**
   * Whether a lot may be lettered at all: somebody the sheet is about lives on
   * it, or it belongs to the sheet's sector and a walker will pass it.
   *
   * A limited sheet drops the second half. Its subject is a list of
   * households, not a stretch of ground, so the sector around them is context
   * — lettering it would put every number in the ward beside the fourteen the
   * reader asked for.
   */
  const letterable = (mine: boolean, ps: { sm?: number; si?: number }[]) =>
    mine || (!limitTo && ps.some(inSector));

  const parcels = all.filter((p) => insideWardGrid(p.c.x, p.c.y));

  // Frame on what this sheet is about: every lot the sector owns, not only
  // the ones with a member behind the door. Seeding on `mine` was right when
  // a sector was a scatter of member households and the lot next door had no
  // sector at all; now that every parcel is assigned, it cropped 149 lots off
  // the sheets of the very walks they belong to — a walker sent to 1.2 got no
  // sheet showing 3378 S 400 E, because the nearest member on that street
  // lives three lots south.
  //
  // The fallbacks still matter in order: a sector with no lots of its own
  // falls back to its households, and a sheet with neither draws the ward
  // rather than nothing.
  //
  // A limited sheet turns that around. Its subject is a list of doors and not
  // a stretch of ground, so it frames on the doors: fourteen households on one
  // street want that street, and the ward around it at ward scale is a page of
  // three-pixel lots where a house number cannot be drawn at all. Where the
  // fourteen are scattered the box comes out near ward-sized anyway, which is
  // the honest answer to a scattered question.
  const sectorParcels = parcels.filter(inSector);
  const mineParcels = parcels.filter((p) => p.mine);
  const focusParcels = limitTo
    ? (mineParcels.length ? mineParcels : sectorParcels)
    : (sectorParcels.length ? sectorParcels : mineParcels);
  const frameSeed = emptyBox();
  for (const p of (focusParcels.length ? focusParcels : parcels)) {
    const b = boxOf(p.rings);
    growBox(frameSeed, { x: b.x0, y: b.y0 });
    growBox(frameSeed, { x: b.x1, y: b.y1 });
  }
  // A household we could not put on a lot inside the ward still has to be
  // accounted for. Split them: ones inside the boundary get a dot on the map,
  // ones outside get a note, because there is nowhere on this sheet to draw
  // them and pretending otherwise would move somebody's house.
  const placed = new Set(parcels.filter((p) => p.mine).flatMap((p) => p.keys));
  const loose = inFocus.filter((h) => !placed.has(lotKey(h)));
  const orphans = loose
    .filter((h) => h.lat != null && h.lng != null
      && insideWardGrid(project(h.lng as number, h.lat as number).x,
                        project(h.lng as number, h.lat as number).y, WARD_PAD))
    .map((h) => ({ ...h, ...project(h.lng as number, h.lat as number) }));
  const outside = loose.filter((h) => !orphans.some((o) => o.key === h.key));
  // The frame is not widened for the unplaceable. Nothing is drawn for them
  // any more — they are rows on the roster — and pulling the map out around a
  // mark that is not there is the failure the old `showDots` guard existed to
  // avoid, now unconditional.

  const contentAspect = (frameSeed.x1 - frameSeed.x0) / (frameSeed.y1 - frameSeed.y0);
  const landscape = orientation === 'landscape'
    || (orientation === 'auto' && contentAspect > 1);
  // Only the width matters now: it sets the scale that label-fitting
  // decisions are made against. Height follows the content's own shape.
  const sheetW = landscape
    ? (sheetPx?.landscape ?? SHEET_LONG)
    : (sheetPx?.portrait ?? SHEET_SHORT);

  const frame = padBox(frameSeed, 18);
  // Measured on the padded frame, which is what the viewBox actually shows.
  // Taking it from the unpadded box left the printed height a few per cent
  // short of the drawing, so the SVG letterboxed inside its own box again.
  const paper = printSize((frame.x1 - frame.x0) / (frame.y1 - frame.y0), landscape);
  const unitsPerPx = (frame.x1 - frame.x0) / sheetW;
  const pxPerUnit = 1 / unitsPerPx;

  const visible = parcels.filter((p) => {
    const b = boxOf(p.rings);
    return b.x1 >= frame.x0 && b.x0 <= frame.x1 && b.y1 >= frame.y0 && b.y0 <= frame.y1;
  });

  // Number what fits; index the rest into a margin list. A parcel at sheet
  // scale can be a few pixels across — Shady Brook's condos are — and a
  // number crammed into one is ink, not information.
  const fontUnits = MIN_LABEL_PX * unitsPerPx;
  const labels: PlatLabel[] = [];
  const listed: { n: number; address: string }[] = [];
  const widthPx = (t: string) => t.length * MIN_LABEL_PX * 0.62;
  // The whole ward on one sheet cannot carry 1,600 house numbers legibly,
  // and a reader looking at the whole ward is not looking for a house — they
  // are looking for where in the ward the members are. The numbers and names
  // come off; the shading, which is what answers that, stays.
  //
  // A limited ward sheet is the opposite case. Fourteen households out of the
  // whole ward is a sheet somebody walks, not a distribution to read, and the
  // gate below letters only the lots the sheet is about — so there are
  // fourteen numbers on it, not sixteen hundred.
  const wardView = focus.kind === 'ward' && !limitTo;

  // Everything that prints words shares one reservation list, and the street
  // names claim their spots first.
  //
  // They used to keep a list of their own, so a street name and a house
  // number could not see each other and "428" printed through "3370 S". The
  // streets go first because they are the sheet's skeleton — a walker finds
  // the road, then the door — and because a street that cannot be placed is
  // simply dropped, while a lot that cannot be lettered still falls back to
  // a number, and then to a numbered entry in the margin.
  const labelBoxes: Box[] = [];

  const inFrame = (p: XY) => p.x >= frame.x0 && p.x <= frame.x1
    && p.y >= frame.y0 && p.y <= frame.y1;
  const roads = data.roads
    .map((r) => ({ n: r.n, pts: r.g.map(([lng, lat]) => project(lng, lat)) }))
    .filter((r) => r.pts.some(inFrame));

  // One label per street, not one per segment. The source splits a street at
  // every intersection, so labelling segments writes "700 E" eight times down
  // the same road and stacks "Millcreek Way" on top of itself. Of everything
  // the sheet shows of a street, take its longest visible run: the stretch
  // with the most room to carry the name.
  const pieces = new Map<string, XY[][]>();
  for (const r of roads) {
    for (const run of clipToFrame(r.pts, frame)) {
      pieces.set(r.n, [...(pieces.get(r.n) ?? []), run]);
    }
  }
  const longest = new Map<string, { seg: XY[]; len: number }>();
  for (const [n, runs] of pieces) {
    // Two units of tolerance: the county's segments share their intersection
    // vertex exactly, and rounding into grid units is all that separates them.
    for (const chain of chainRuns(runs, unitsPerPx * 2)) {
      const len = arcLengths(chain).pop() ?? 0;
      const prev = longest.get(n);
      if (!prev || len > prev.len) longest.set(n, { seg: chain, len });
    }
  }


  // Streets cross, and at a crossing two names land on the same spot and
  // print through each other. Try several positions along each street and
  // take the first that is clear; if none is, leave the street unlabelled.
  // An unreadable name is worse than a missing one — the reader cannot tell
  // it is unreadable without stopping to work out why.
  const fs = STREET_LABEL_PX * unitsPerPx;
  const textWidth = (name: string) => name.length * fs * 0.58;
  const boxFor = (name: string, x: number, y: number, deg: number): Box => {
    const w = textWidth(name);
    const h = fs * 1.15;
    const r = (deg * Math.PI) / 180;
    const aw = Math.abs(w * Math.cos(r)) + Math.abs(h * Math.sin(r));
    const ah = Math.abs(w * Math.sin(r)) + Math.abs(h * Math.cos(r));
    return { x0: x - aw / 2, x1: x + aw / 2, y0: y - ah / 2, y1: y + ah / 2 };
  };
  const clear = (b: Box) => !labelBoxes.some(
    (o) => b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0);

  // Don't label a stub: a name needs a road long enough to carry it. Now that
  // the run is measured on what the sheet actually shows, the name's own
  // width is the test — a street with four units of itself in the corner of
  // the sheet cannot hold "LORRAINE AVE", and printing it there put the name
  // on the parcels beside the road rather than on the road. A little over the
  // width, so the name does not run wall to wall; and a floor, so a
  // two-character name cannot claim a sliver on the same argument.
  const minFor = (name: string) =>
    Math.max(textWidth(name) * 1.05, 26 * unitsPerPx);

  const streetLabels: { name: string; x: number; y: number; deg: number }[] = [];
  const candidates = [...longest.entries()]
    .filter(([n, v]) => v.len >= minFor(n))
    // Longest first: a major road earns the contested spot at a junction,
    // and a stub cul-de-sac is the one that can go without.
    .sort((a, b) => b[1].len - a[1].len);

  for (const [name, v] of candidates) {
    const use = v.seg;
    const cum = arcLengths(use);
    const total = cum[cum.length - 1];
    // Half the run the text will cover, so the angle below is taken from the
    // stretch of road the name actually sits on rather than from the whole
    // segment: on a curve those differ, and the chord of the whole curve
    // leaves the name lying across the road instead of along it.
    const half = Math.min(textWidth(name) / 2, total / 2);

    // Midpoint first, then progressively off-centre. Measured in length, not
    // in vertices: a straight street is two points, and stepping to the
    // "middle" vertex of two lands on an end. Junctions sit at the ends of a
    // run, so the middle is usually clear and the fallbacks walk out from it.
    const order = [0.5, 0.38, 0.62, 0.28, 0.72, 0.18, 0.82];
    for (const t of order) {
      const d = t * total;
      const at = pointAt(use, cum, d);
      const a = pointAt(use, cum, d - half), b = pointAt(use, cum, d + half);
      let deg = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      // Keep text upright: a street running right-to-left would otherwise
      // print its name upside down.
      if (deg > 90) deg -= 180;
      if (deg < -90) deg += 180;
      const box = boxFor(name, at.x, at.y, deg);
      if (clear(box)) {
        labelBoxes.push(box);
        streetLabels.push({ name, x: at.x, y: at.y, deg });
        break;
      }
    }
  }


  // Parcel labels collide too — adjacent condo buildings put their names a
  // few units apart. Ours are placed first so a directory household never
  // loses its label to a neighbour we do not visit.
  // Measured on the block's widest line, not on one line of it: a label that
  // runs to a number over two surnames is as wide as its longest row, and
  // reserving less lets a neighbour overlap it.
  //
  // Reserved where the text is actually drawn — the union of its placed
  // lines — not at the middle of the lot. The lot's centre was what this used
  // to measure, and an anchored label can sit a long way from it: 404 and 406
  // E 3335 S are separate slivers whose centres are clear of each other,
  // while both labels get pulled to the same open ground and printed through
  // each other.
  const fits = (text: string | string[], at: XY[]) => {
    const wide = Math.max(...[text].flat().map(widthPx));
    const w = wide * unitsPerPx, h = fontUnits * 1.1;
    const b = {
      x0: Math.min(...at.map((q) => q.x)) - w / 2,
      x1: Math.max(...at.map((q) => q.x)) + w / 2,
      y0: Math.min(...at.map((q) => q.y)) - h / 2,
      y1: Math.max(...at.map((q) => q.y)) + h / 2,
    };
    if (labelBoxes.some((o) => b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0)) {
      return false;
    }
    labelBoxes.push(b);
    return true;
  };

  // A multi-unit building is one label, not one per unit. The county maps
  // each condo unit as its own polygon — The Hudson is 114 — so labelling
  // parcels would print "566" a hundred and fourteen times over one
  // building. Draw every footprint, name the building once, at the middle.
  const groups = new Map<string, typeof visible>();
  for (const p of visible) {
    if (p.k) groups.set(p.k, [...(groups.get(p.k) ?? []), p]);
  }
  for (const [, ps] of groups) {
    if (wardView) break;
    const b = emptyBox();
    for (const p of ps) {
      const pb = boxOf(p.rings);
      growBox(b, { x: pb.x0, y: pb.y0 });
      growBox(b, { x: pb.x1, y: pb.y1 });
    }
    // The bounding box still decides whether there is a lot worth lettering
    // at all (`wPx`/`hPx` below). Where the text sits, and therefore what it
    // clashes with, is the anchor's business — this used to test the clash at
    // the box centre and draw at the anchor, which is how two labels ended up
    // on top of each other.
    const rings = ps.flatMap((p) => p.rings);
    const mine = ps.some((p) => p.mine);
    // Nothing is written on a lot outside this sheet's sector. A walker is
    // looking for their own doors, and forty numbers they will not knock on
    // is forty things to read past. The lot is still drawn, faintly, so the
    // street still has its shape.
    //
    // Unless somebody this sheet is about lives on it, which happens where a
    // household matched a lot in the neighbouring sector — three times in the
    // ward. A shaded house with no number on it would be a worse puzzle than
    // a number in the wrong sector. See `letterable`.
    if (!letterable(mine, ps)) continue;
    // Road furniture carries a county house number and no door; see
    // PlatParcel.roadFurniture. Drawn, never lettered.
    if (!mine && ps.every((p) => p.roadFurniture)) continue;
    const num = houseNumber(ps[0].address);
    const pn = ps.find((p) => p.name)?.name ?? null;
    // The roster across the whole property, not whichever of its parcels
    // happened to carry it — a property's households are spread over its
    // lots, and the county maps each condo unit separately.
    //
    // Multi-unit is checked here as well as per parcel. A building with no
    // name has no `pn` to stop it, and picking two of its thirty-two
    // households would put their names on the front of it.
    const sn = pn || ps.some((p) => p.mu) ? [] : rosterOn(
      [...new Set(ps.flatMap((p) => p.keys))].filter((k) => focusKeys.has(k)));
    const fullNames = pn ? [pn] : sn.map((e) => e.name);
    const full = pn ? [num, pn].filter((t): t is string => !!t)
                    : labelLines(num, sn);
    const short = pn ? [num, pn].filter((t): t is string => !!t)
                     : labelLines(num, sn.slice(0, 1));
    // Deliberately the bounding box, not the narrower run the label is placed
    // in. Judging the fit on the run would drop the number from nine lots
    // that carry one legibly today — an irregular lot is pinched somewhere,
    // and a number centred in the wide part of it reads fine even where it
    // overhangs the pinch. Placement handles the overhang; this decides only
    // whether there is a lot worth lettering at all.
    const wPx = (b.x1 - b.x0) * pxPerUnit;
    const hPx = (b.y1 - b.y0) * pxPerUnit;
    // Same ladder a single lot uses: number over name where both fit, then
    // number alone, then an indexed margin entry. The number is what is on
    // the door, so it should be the last thing to go, not the first.
    // Placed before it is tested: a label is reserved against its neighbours
    // where it is drawn, not where the lot's middle happens to be, or two
    // labels that never touch can knock each other off the sheet.
    //
    // The anchor is asked for per label rather than once per lot, because
    // where the text fits depends on how wide the text is: a number alone
    // sits where a number over two surnames cannot.
    const hatched = ps.some((p) => p.mu);
    const one = () => {
      const w = widthPx(num ?? '') * unitsPerPx;
      const a = labelAnchor(rings, w);
      return placeLine(rings, a.x, a.y, fontUnits * 0.35, w);
    };
    // Place, then test where it landed, then draw. See `fits`.
    const draw = (lines: string[]) => {
      const w = Math.max(...lines.map(widthPx)) * unitsPerPx;
      const a = labelAnchor(rings, w);
      const at = placeBlock(rings, a.x, a.y, lineOffsets(lines.length, fontUnits), w);
      if (!at || !fits(lines, at)) return false;
      lines.forEach((text, i) => labels.push(
        { ...at[i], text, bold: mine, halo: hatched }));
      return true;
    };
    const drawOne = () => {
      const at = one();
      if (!fits(num ?? '', [at])) return false;
      labels.push({ ...at, text: num as string, bold: mine, halo: hatched });
      return true;
    };
    // The whole roster where the lot can carry it, the first household where
    // it cannot. The fallback is a number over one surname — what every named
    // lot showed before, so growing the label never costs a lot its name.
    // Width is judged on the surnames, not on the house numbers. A number is
    // short, centred and reads fine overhanging a narrow lot by a character;
    // the old two-line test measured the name alone for that reason, and
    // measuring the number too would cost lots like 1141 E Ridgedale Cir the
    // label they carry today.
    const room = (lines: string[], names: string[]) => lines.length > 1
      && hPx >= MIN_LABEL_PX * (1.15 * lines.length + 0.3)
      && (!names.length || wPx >= Math.max(...names.map(widthPx)));
    if (num && room(full, fullNames) && draw(full)) {
      // drawn
    } else if (num && room(short, fullNames.slice(0, 1)) && draw(short)) {
      // drawn
    } else if (num && wPx >= widthPx(num) && drawOne()) {
      // drawn
    } else if (mine) {
      const who = pn ?? sn.map((e) => e.name).join(' & ');
      const entry = `${ps[0].address ?? num}${who ? ` — ${who}` : ''}`;
      listed.push({ n: listed.length + 1, address: entry });
      // The margin index is the last resort for a household of ours, so it is
      // drawn whether or not the spot is clear — losing it would lose the
      // house. It still reserves its ground so nothing later stacks on it,
      // and where it could not get clear ground it takes a halo, which is the
      // only thing that makes a digit readable over a street name.
      const at = one();
      labels.push({ ...at, text: String(listed.length), bold: true,
                    halo: hatched || !fits(String(listed.length), [at]) });
    }
  }

  const singles = visible.filter((p) => !p.k)
    .sort((a, b) => Number(b.mine) - Number(a.mine));
  for (const p of wardView ? [] : singles) {
    const num = houseNumber(p.address);
    if (!num) continue;
    // See the group loop above.
    if (!letterable(p.mine, [p])) continue;
    if (!p.mine && p.roadFurniture) continue;
    const b = boxOf(p.rings);
    const wPx = (b.x1 - b.x0) * pxPerUnit;
    const hPx = (b.y1 - b.y0) * pxPerUnit;
    // As above: the box decides whether to letter the lot, the anchor decides
    // where the text goes and what it has to clear.
    const bold = p.mine;

    // Try the fullest label that fits, then fall back. A directory household
    // gets its surname under the number where there is room for two lines.
    const fullNames = p.name ? [p.name] : p.roster.map((e) => e.name);
    const full = p.name ? [num, p.name] : labelLines(num, p.roster);
    const short = p.name ? [num, p.name] : labelLines(num, p.roster.slice(0, 1));
    // As in the group loop: the anchor depends on the width of the text, so
    // each candidate label asks for its own.
    const one = () => {
      const w = widthPx(num) * unitsPerPx;
      const a = labelAnchor(p.rings, w);
      return placeLine(p.rings, a.x, a.y, fontUnits * 0.35, w);
    };
    // Place, then test where it landed, then draw. See `fits`.
    const draw = (lines: string[]) => {
      const w = Math.max(...lines.map(widthPx)) * unitsPerPx;
      const a = labelAnchor(p.rings, w);
      const at = placeBlock(p.rings, a.x, a.y, lineOffsets(lines.length, fontUnits), w);
      if (!at || !fits(lines, at)) return false;
      lines.forEach((text, i) => labels.push(
        { ...at[i], text, bold, halo: p.mu }));
      return true;
    };
    const drawOne = () => {
      const at = one();
      if (!fits(num, [at])) return false;
      labels.push({ ...at, text: num, bold, halo: p.mu });
      return true;
    };
    // See the group loop: the surnames decide the width, the numbers do not.
    const room = (lines: string[], names: string[]) => lines.length > 1
      && hPx >= MIN_LABEL_PX * (1.15 * lines.length + 0.3)
      && (!names.length || wPx >= Math.max(...names.map(widthPx)));
    if (room(full, fullNames) && draw(full)) {
      // drawn
    } else if (room(short, fullNames.slice(0, 1)) && draw(short)) {
      // drawn
    } else if (wPx >= widthPx(num) && hPx >= MIN_LABEL_PX * 1.25 && drawOne()) {
      // drawn
    } else if (p.mine) {
      // Too small to letter, and ours: index it into the margin list.
      const who = p.name ?? p.roster.map((e) => e.name).join(' & ');
      listed.push({ n: listed.length + 1,
                    address: `${p.address ?? num}${who ? ` — ${who}` : ''}` });
      // See the group loop: always drawn, always reserved, haloed if crowded.
      const at = one();
      labels.push({ ...at, text: String(listed.length), bold,
                    halo: p.mu || !fits(String(listed.length), [at]) });
    }
    // Too small and not ours: no label. A number nobody can read is noise.
  }

  // Merge a property's footprints. Three parcels of one address are one
  // place to stand, and the lot lines between them are a distinction the
  // assessor cares about and a person walking the street does not.
  //
  // Rather than a polygon union, drop every edge that two parcels of the
  // same group share and keep the rest: what remains is the outline. The
  // county's rings meet exactly, so shared edges match once rounded. Where
  // they do not, the interior line simply stays — the shape is still right.
  const merged: { d: Seg[]; mine: boolean; inSector: boolean }[] = [];
  const groupedIds = new Set<typeof visible[number]>();
  const groupMine = new Map<string, boolean>();
  const groupMu = new Map<string, boolean>();
  for (const [k, ps] of groups) {
    groupMine.set(k, ps.some((p) => p.mine));
    groupMu.set(k, ps.some((p) => p.mu));
  }
  for (const [, ps] of groups) {
    if (ps.length < 2) continue;
    ps.forEach((p) => groupedIds.add(p));
    const d = outlineOf(ps);
    if (d.length) {
      merged.push({ d, mine: ps.some((p) => p.mine),
                    inSector: ps.some(inSector) });
    }
  }

  // Resolve grouping into flags the renderers can draw from directly.
  // Which sector each lot is in, and whether that is this sheet's sector.
  // Every lot inside the ward carries one now, so a sector has a shape rather
  // than a scatter of shaded lots.
  const drawn: PlatParcel[] = visible.map((p) => ({
    ...p,
    inSector: inSector(p),
    outlined: !groupedIds.has(p),
    filled: p.k ? !!groupMine.get(p.k) : p.mine,
    // Follows the property rather than the parcel, as the shading does. The
    // county maps each condo unit separately — The Hudson is 114 polygons —
    // and hatching only the units that happen to carry the flag would draw
    // stripes across part of a building and leave the rest plain.
    striped: p.k ? !!groupMu.get(p.k) : p.mu,
  }));

  return { parcels: drawn, roads, frame, labels, listed, orphans, outside,
           unitsPerPx, streetLabels, landscape, merged, wardView, paper };
}
